import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

interface AltoADetailProps {
  t: any;
  isRTL: boolean;
}

const ProductDetailPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 120px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  padding-bottom: 4rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    text-decoration: ${props => props.$isLast ? 'none' : 'underline'};
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #94a3b8;
  margin: 0 0.5rem;
`;

const BreadcrumbDropdown = styled.span`
  color: #94a3b8;
  margin-left: 0.5rem;
  cursor: pointer;
`;

const ProductLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

const MainImage = styled.div`
  width: 100%;
  max-width: 500px;
  height: 400px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const ThumbnailImage = styled.div<{ $isSelected: boolean }>`
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px solid ${props => props.$isSelected ? '#dc2626' : '#e2e8f0'};
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    border-color: #dc2626;
    transform: translateY(-2px);
  }
`;

const ThumbnailsContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    align-items: center;
  }
`;

const ProductTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ProductDescription = styled.p`
  font-size: 1.1rem;
  color: #475569;
  line-height: 1.6;
  margin: 0;
`;

const TechnicalDatasheet = styled(motion.div)<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 300px;
  
  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const PDFIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #dc2626;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.8rem;
`;

const PDFText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PDFLabel = styled.span`
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
`;

const PDFSubtext = styled.span`
  font-size: 0.8rem;
  color: #64748b;
`;

const ProviderLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 300px;
  
  img {
    max-width: 120px;
    height: auto;
    object-fit: contain;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    max-width: 250px;
    align-self: center;
    margin: 1rem auto;
    
    img {
      max-width: 100px;
    }
  }
`;

const TabsSection = styled.div`
  margin-top: 3rem;
`;

const TabsHeader = styled.div`
  display: flex;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 2rem;
  gap: 0.5rem;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 1rem 1.5rem;
  border: none;
  background: none;
  font-weight: 600;
  cursor: pointer;
  color: ${props => props.$active ? '#dc2626' : '#64748b'};
  border-bottom: 2px solid ${props => props.$active ? '#dc2626' : 'transparent'};
  transition: all 0.3s ease;
  font-size: 1rem;

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    color: #dc2626;
  }
`;

const TabContent = styled(motion.div)`
  min-height: 400px;
`;

const DescriptionSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #dc2626;
  padding-bottom: 0.5rem;
`;

const DescriptionText = styled.p`
  color: #475569;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 1rem;
`;

const SpecsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SpecItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #dc2626;
`;

const SpecLabel = styled.span`
  font-weight: 600;
  color: #1e293b;
  min-width: 120px;
`;

const SpecValue = styled.span`
  color: #475569;
  flex: 1;
`;

const ResourcesSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const ResourceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    background: #e2e8f0;
    transform: translateY(-1px);
  }
`;

const ResourceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ResourceName = styled.span`
  font-weight: 600;
  color: #1e293b;
`;

const ResourceSize = styled.span`
  font-size: 0.9rem;
  color: #64748b;
`;

const DownloadLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #dc2626;
  text-decoration: none;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    align-self: center;
    margin: 1rem auto;
  }
  
  &:hover {
    background: #dc2626;
    color: white;
  }
`;

const AltoADetail: React.FC<AltoADetailProps> = ({ t, isRTL }) => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [activeTab, setActiveTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(0);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: "Catalogue produits", path: "/" },
    { label: "Postes préfabriqués", path: "/category/postes-prefabriques" },
    { label: "ALTO A", path: "" }
  ];

  const images = [
    '/images/catalog/postes/ALTO A.jpg'
  ];

  const handleBreadcrumbClick = (path: string) => {
    if (path) {
      navigate(path);
    }
  };

  const handleDatasheetClick = () => {
    window.open('/images/catalog/postes/ALTO A/Nexans_ALTO-A.pdf', '_blank');
  };

  return (
    <ProductDetailPage $isRTL={isRTL}>
      <Container>
        <Breadcrumbs
          $isRTL={isRTL}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbLink
                $isLast={index === breadcrumbs.length - 1}
                onClick={() => handleBreadcrumbClick(breadcrumb.path)}
              >
                {breadcrumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && <BreadcrumbSeparator>›</BreadcrumbSeparator>}
              {index === breadcrumbs.length - 1 && <BreadcrumbDropdown>▼</BreadcrumbDropdown>}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <ProductLayout>
          <ImageSection>
            <MainImage>
              <img src={images[selectedImage]} alt="ALTO A" />
            </MainImage>
            <ThumbnailsContainer>
              {images.map((image, index) => (
                <ThumbnailImage
                  key={index}
                  $isSelected={selectedImage === index}
                  onClick={() => setSelectedImage(index)}
                >
                  <img src={image} alt={`ALTO A ${index + 1}`} />
                </ThumbnailImage>
              ))}
            </ThumbnailsContainer>
          </ImageSection>

          <ProductInfo>
            <ProductTitle>ALTO A</ProductTitle>
            <ProductDescription>
              Poste préfabriqué de transformation MT/BT à comptage BT
            </ProductDescription>
            <TechnicalDatasheet
              $isRTL={isRTL}
              onClick={handleDatasheetClick}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <PDFIcon>PDF</PDFIcon>
              <PDFText>
                <PDFLabel>Fiche technique</PDFLabel>
                <PDFSubtext>PDF</PDFSubtext>
              </PDFText>
            </TechnicalDatasheet>

            <ProviderLogo>
              <img 
                src="/images/partners/nexans.png" 
                alt="Nexans" 
              />
            </ProviderLogo>
          </ProductInfo>
        </ProductLayout>

        <TabsSection ref={ref}>
          <TabsHeader>
            <TabButton 
              $active={activeTab === 'description'} 
              onClick={() => setActiveTab('description')}
            >
              Description
            </TabButton>
            <TabButton 
              $active={activeTab === 'ressources'} 
              onClick={() => setActiveTab('ressources')}
            >
              Ressources
            </TabButton>
          </TabsHeader>

          <TabContent>
            {activeTab === 'description' && (
              <DescriptionSection>
                <SectionTitle>Description</SectionTitle>
                
                <SpecsList>
                  <SpecItem>
                    <SpecLabel>Normes</SpecLabel>
                    <SpecValue>
                      <strong>Internationales CEI:</strong> 62 271 202
                    </SpecValue>
                  </SpecItem>
                  
                  <SpecItem>
                    <SpecLabel>Description</SpecLabel>
                    <SpecValue>
                      Poste préfabriqué de transformation MT/BT à comptage BT.<br/><br/>
                      Il peut être pour abonné ou de distribution publique.<br/><br/>
                      Alto A est un poste de transformation MT/BT pour les réseaux « en antenne ». Il est équipé d'un transformateur et des départs BT en fusibles ou en disjoncteurs.
                    </SpecValue>
                  </SpecItem>
                  
                  <SpecItem>
                    <SpecLabel>Caractéristiques</SpecLabel>
                    <SpecValue>
                      Pour plus d'information télécharger la fiche technique
                    </SpecValue>
                  </SpecItem>
                </SpecsList>
              </DescriptionSection>
            )}

            {activeTab === 'ressources' && (
              <ResourcesSection>
                <SectionTitle>Ressources</SectionTitle>
                
                <ResourceItem>
                  <ResourceInfo>
                    <ResourceName>Technical data ALTO A (french only)</ResourceName>
                    <ResourceSize>PDF — 178.4 KB</ResourceSize>
                  </ResourceInfo>
                  <DownloadLink 
                    href="/images/catalog/postes/ALTO A/ressources/ALTO A.pdf"
                    target="_blank"
                  >
                    <span>↓</span>
                    Download
                  </DownloadLink>
                </ResourceItem>
              </ResourcesSection>
            )}
          </TabContent>
        </TabsSection>
      </Container>
      <Footer t={t} isRTL={isRTL} />
    </ProductDetailPage>
  );
};

export default AltoADetail; 